import { useEffect, useRef, useState } from 'react';
import type { Detection } from '@/utils/yoloPostProcess';

type DetectionFrame = {
  timestamp: number;
  detections: Detection[];
};

const DETECTIONS_URL = '/detections.json';

export type DetectionHookReturn = {
  predictionsRef: React.MutableRefObject<Detection[]>;
  loading: boolean;
  error: string | null;
};

function findNearestFrame(
  frames: DetectionFrame[],
  time: number
): DetectionFrame {
  let lo = 0;
  let hi = frames.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (frames[mid].timestamp < time) lo = mid + 1;
    else hi = mid;
  }
  if (
    lo > 0 &&
    Math.abs(frames[lo - 1].timestamp - time) <
      Math.abs(frames[lo].timestamp - time)
  ) {
    return frames[lo - 1];
  }
  return frames[lo];
}

export function useObjectDetection(
  videoContainerRef: React.RefObject<HTMLDivElement | null>,
  enabled: boolean
): DetectionHookReturn {
  const predictionsRef = useRef<Detection[]>([]);
  const framesRef = useRef<DetectionFrame[]>([]);
  const loadedRef = useRef(false);
  const rafRef = useRef<number>(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load detections JSON once on first enable
  useEffect(() => {
    if (!enabled || loadedRef.current) return;

    loadedRef.current = true;
    setLoading(true);

    fetch(DETECTIONS_URL)
      .then((r) => r.json())
      .then((data: DetectionFrame[]) => {
        framesRef.current = data;
        setLoading(false);
      })
      .catch((err) => {
        setError(
          err instanceof Error ? err.message : 'Failed to load detections'
        );
        setLoading(false);
      });
  }, [enabled]);

  // Sync predictions with video currentTime each animation frame
  useEffect(() => {
    if (!enabled) {
      predictionsRef.current = [];
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }
      return;
    }

    const syncFrame = () => {
      const video = videoContainerRef.current?.querySelector('video');
      if (video && framesRef.current.length > 0) {
        const frame = findNearestFrame(framesRef.current, video.currentTime);
        predictionsRef.current = frame.detections;
      }
      rafRef.current = requestAnimationFrame(syncFrame);
    };

    rafRef.current = requestAnimationFrame(syncFrame);
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }
    };
  }, [enabled, videoContainerRef]);

  return { predictionsRef, loading, error };
}
